import { addWeeks } from "date-fns"

import {
  convertToStartOfMonthUTC,
  convertToStartOfWeekUTC,
  getMonthDatesBetween,
  getStartOfCurrentMonthUTC,
  getStartOfCurrentWeekUTC,
} from "~/utils/datetime"
import type { DateQuantityMap } from "~/utils/variantTable/types"
import { processTimeSeriesData } from "~/utils/variantTable/timeSeriesProcessor"

export const PERF_WEEKDIFF_MARGIN = 12

export type PerformanceForecastByVariant = Map<
  string,
  { weekDiff: number; firstDay: Date; forecast: number[] }
>

export type PerformanceVariantStats = {
  variantId: string
  totalSales: number
  totalExpectedDemand: number
  performanceIndex: number | null
  overlapPeriods: number
}

export type PerformanceLookback = {
  /** Sorted, deduplicated weekDiff values across the selected variants; empty when no data. */
  weekDiffs: number[]
  min: number | null
  max: number | null
}

export type PerformanceSummary = {
  performanceIndex: number | null
  totalSales: number
  totalExpectedDemand: number
  difference: number
  mae: number
  overlapPeriods: number
  periodUnit: "week" | "month"
}

export function buildExpectedDemandByVariant(
  performanceMap: PerformanceForecastByVariant | undefined,
  currentWeekMs: number,
  monthlyView: boolean,
): Map<string, Map<number, number>> {
  const result = new Map<string, Map<number, number>>()
  if (!performanceMap) return result
  // Buckets to the month containing each week's Monday, so this shares keys with the sales map.
  const bucketDate = (d: Date) =>
    monthlyView ? convertToStartOfMonthUTC(d) : convertToStartOfWeekUTC(d)

  for (const [variantId, perf] of performanceMap) {
    const map = new Map<number, number>()
    const firstDay = convertToStartOfWeekUTC(perf.firstDay)
    for (let i = 0; i < perf.forecast.length; i++) {
      const weekDate = addWeeks(firstDay, i + perf.weekDiff)
      const ms = weekDate.getTime()
      // Drop future weeks; keep the current one (the table renders it too).
      if (ms > currentWeekMs) continue
      const bucketMs = bucketDate(weekDate).getTime()
      const value = perf.forecast[i] ?? 0
      map.set(bucketMs, (map.get(bucketMs) ?? 0) + value)
    }
    result.set(variantId, map)
  }
  return result
}

export type AggregatedSalesRow = {
  variantId: string
  sales: { dateString: string; quantity: number }[]
}

export function buildSalesByVariant(
  rawSales: Record<string, AggregatedSalesRow> | undefined,
  futureCutoffMs: number,
  monthlyView: boolean,
): Map<string, Map<number, number>> {
  const result = new Map<string, Map<number, number>>()
  if (!rawSales) return result
  const normalize = monthlyView ? convertToStartOfMonthUTC : convertToStartOfWeekUTC
  for (const sku of Object.values(rawSales)) {
    const map = result.get(sku.variantId) ?? new Map<number, number>()
    for (const row of sku.sales) {
      const ms = normalize(new Date(row.dateString)).getTime()
      if (ms > futureCutoffMs) continue
      map.set(ms, (map.get(ms) ?? 0) + row.quantity)
    }
    result.set(sku.variantId, map)
  }
  return result
}

export function aggregateWeekly(maps: Iterable<Map<number, number>>): Map<number, number> {
  const result = new Map<number, number>()
  for (const map of maps) {
    for (const [ms, v] of map) {
      result.set(ms, (result.get(ms) ?? 0) + v)
    }
  }
  return result
}

export function toDateQuantityMap(
  weekly: Map<number, number>,
  startDate: Date,
  endDate: Date,
  monthlyView: boolean,
): DateQuantityMap {
  if (weekly.size === 0) return new Map()
  const series = [...weekly.entries()].map(([ms, qty]) => ({
    date: new Date(ms),
    quantity: qty as number | null,
  }))
  // null fiscalConfig: calendar months, and skips the client-only default (unhydrated server-side).
  const processed = processTimeSeriesData(series, startDate, endDate, monthlyView, false, null)
  return new Map(processed.map((d) => [d.date.getTime(), d.quantity]))
}

/** Fills missing months with null; skips processTimeSeriesData, which assumes weekly input. */
export function buildMonthlyDateQuantityMap(
  monthly: Map<number, number>,
  startDate: Date,
  endDate: Date,
): DateQuantityMap {
  const result: DateQuantityMap = new Map()
  for (const month of getMonthDatesBetween(startDate, endDate)) {
    const ms = convertToStartOfMonthUTC(month).getTime()
    result.set(ms, monthly.has(ms) ? monthly.get(ms)! : null)
  }
  return result
}

export function computeVariantStats(
  variantId: string,
  expected: Map<number, number>,
  sales: Map<number, number>,
): PerformanceVariantStats {
  let totalSales = 0
  let totalExpectedDemand = 0
  let overlapPeriods = 0
  for (const [ms, e] of expected) {
    if (!sales.has(ms)) continue
    totalSales += sales.get(ms)!
    totalExpectedDemand += e
    overlapPeriods++
  }
  const performanceIndex = totalExpectedDemand > 0 ? (totalSales / totalExpectedDemand) * 100 : null
  return { variantId, totalSales, totalExpectedDemand, performanceIndex, overlapPeriods }
}

/** Uses the post-aggregation maps so tile totals match the table at the active granularity. */
export function computeSummaryFromAggregated(
  expected: DateQuantityMap,
  sales: DateQuantityMap,
  periodUnit: "week" | "month",
): PerformanceSummary {
  let totalSales = 0
  let totalExpectedDemand = 0
  let absErrorSum = 0
  let overlapPeriods = 0
  for (const [ms, e] of expected) {
    if (e == null) continue
    const s = sales.get(ms)
    if (s == null) continue
    totalSales += s
    totalExpectedDemand += e
    absErrorSum += Math.abs(e - s)
    overlapPeriods++
  }
  return {
    performanceIndex: totalExpectedDemand > 0 ? (totalSales / totalExpectedDemand) * 100 : null,
    totalSales,
    totalExpectedDemand,
    difference: totalSales - totalExpectedDemand,
    mae: overlapPeriods > 0 ? absErrorSum / overlapPeriods : 0,
    overlapPeriods,
    periodUnit,
  }
}

export function computeLookback(
  performanceMap: PerformanceForecastByVariant | undefined,
  variantIds: string[],
): PerformanceLookback {
  const weekDiffSet = new Set<number>()
  if (performanceMap) {
    for (const variantId of variantIds) {
      const perf = performanceMap.get(variantId)
      if (perf) weekDiffSet.add(perf.weekDiff)
    }
  }
  const weekDiffs = [...weekDiffSet].sort((a, b) => a - b)
  return { weekDiffs, min: weekDiffs[0] ?? null, max: weekDiffs[weekDiffs.length - 1] ?? null }
}

export type ComputePerformanceParams = {
  performanceMap: PerformanceForecastByVariant | undefined
  rawSales: Record<string, AggregatedSalesRow> | undefined
  variantIds: string[]
  startDate: Date
  endDate: Date
  monthlyView: boolean
}

/** Single source for the Performance tab's join/aggregation — never re-derive this elsewhere. */
export function computePerformance({
  performanceMap,
  rawSales,
  variantIds,
  startDate,
  endDate,
  monthlyView,
}: ComputePerformanceParams): {
  summary: PerformanceSummary
  variantStats: PerformanceVariantStats[]
  lookback: PerformanceLookback
  expectedDemandMap: DateQuantityMap
  salesMap: DateQuantityMap
} {
  const currentWeekMs = getStartOfCurrentWeekUTC().getTime()
  const futureCutoffMs = monthlyView ? getStartOfCurrentMonthUTC().getTime() : currentWeekMs

  const expectedByVariant = buildExpectedDemandByVariant(performanceMap, currentWeekMs, monthlyView)
  const salesByVariant = buildSalesByVariant(rawSales, futureCutoffMs, monthlyView)

  const aggregatedExpected = aggregateWeekly(expectedByVariant.values())
  const aggregatedSales = aggregateWeekly(salesByVariant.values())

  const expectedDemandMap = monthlyView
    ? buildMonthlyDateQuantityMap(aggregatedExpected, startDate, endDate)
    : toDateQuantityMap(aggregatedExpected, startDate, endDate, monthlyView)
  const salesMap = monthlyView
    ? buildMonthlyDateQuantityMap(aggregatedSales, startDate, endDate)
    : toDateQuantityMap(aggregatedSales, startDate, endDate, monthlyView)

  const summary = computeSummaryFromAggregated(
    expectedDemandMap,
    salesMap,
    monthlyView ? "month" : "week",
  )

  const variantStats: PerformanceVariantStats[] = variantIds.map((variantId) =>
    computeVariantStats(
      variantId,
      expectedByVariant.get(variantId) ?? new Map(),
      salesByVariant.get(variantId) ?? new Map(),
    ),
  )

  return {
    summary,
    variantStats,
    lookback: computeLookback(performanceMap, variantIds),
    expectedDemandMap,
    salesMap,
  }
}
